import { Link } from 'react-router-dom'
import { Download, Plus, RefreshCw } from 'lucide-react'
import { useBenefitRules } from '@/modules/benefits/hooks/useBenefitRules'
import { useBenefitRuleMutation } from '@/modules/benefits/hooks/useBenefitRuleMutation'
import { useBenefitStats } from '@/modules/benefits/hooks/useBenefitStats'
import { BenefitRulesTable } from '@/modules/benefits/components/BenefitRulesTable'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'
import { Can } from '@/modules/auth/components/Can'
import { Permission } from '@/modules/auth/permissions/permissions'
import { exportToExcel } from '@/shared/utils/exportToExcel'

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  }

  return (
    <div className={`card p-5 border ${colorClasses[color] ?? colorClasses.gray}`}>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-sm mt-1 opacity-80">{label}</p>
    </div>
  )
}

export function BenefitRulesListPage() {
  const {
    data,
    total,
    isLoading,
    error,
    filters,
    setPage,
    setLimit,
    applyFilters,
    clearFilters,
    refetch,
  } = useBenefitRules()

  const { remove } = useBenefitRuleMutation()

  const {
    stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useBenefitStats()

  const handleDelete = async (id: number) => {
    await remove(id)
    refetch()
  }

  // suppress unused var — handleDelete is available for future use when table supports it
  void handleDelete

  const handleExport = () => {
    exportToExcel(data, 'reglas_beneficio', [
      { key: 'id', label: 'ID' },
      { key: 'name', label: 'Nombre' },
      { key: 'trigger_type', label: 'Gatillo' },
      { key: 'action_type', label: 'Acción' },
      { key: 'is_active', label: 'Activa' },
      { key: 'priority', label: 'Prioridad' },
    ])
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Beneficios</h1>
        <div className="flex gap-2">
          <button
            onClick={refetchStats}
            disabled={statsLoading}
            className="btn btn-secondary flex items-center gap-1.5"
          >
            <RefreshCw size={16} className={statsLoading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <Link
            to="/benefits/backfill"
            className="btn btn-secondary flex items-center gap-1.5"
          >
            Asignación retroactiva
          </Link>
          <Can permission={Permission.BENEFITS_EXPORT}>
            <button onClick={handleExport} className="btn btn-secondary flex items-center gap-1.5">
              <Download size={16} />
              Exportar
            </button>
          </Can>
          <Link to="/benefits/new" className="btn btn-primary flex items-center gap-1.5">
            <Plus size={16} />
            Nueva Regla
          </Link>
        </div>
      </div>

      {/* Stats section */}
      {statsError && <ErrorState message={statsError} onRetry={refetchStats} />}

      {statsLoading && !stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard label="Activos este mes" value={stats.active_this_month} color="green" />
            <StatCard label="Usados este mes" value={stats.used_this_month} color="blue" />
            <StatCard label="Expirados este mes" value={stats.expired_this_month} color="gray" />
            <StatCard label="Vales pendientes" value={stats.pending_vouchers} color="yellow" />
            <StatCard label="Reglas activas" value={stats.rules_active} color="purple" />
          </div>

          {/* Per-rule breakdown */}
          {stats.by_rule.length > 0 && (
            <div className="card mb-8">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Detalle por Regla</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Regla</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Otorgados</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Usados</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">% Uso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_rule.map((rule) => {
                      const usageRate = rule.granted > 0
                        ? Math.round((rule.used / rule.granted) * 100)
                        : 0
                      return (
                        <tr key={rule.rule_id} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link
                              to={`/benefits/${rule.rule_id}`}
                              className="text-primary-600 hover:text-primary-700 font-medium"
                            >
                              {rule.rule_name}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{rule.granted}</td>
                          <td className="px-4 py-3 text-right font-medium">{rule.used}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              usageRate >= 75
                                ? 'bg-green-100 text-green-800'
                                : usageRate >= 40
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {usageRate}%
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Rules table */}
      {error && <ErrorState message={error} onRetry={refetch} />}

      <div className="card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Reglas de Beneficio</h2>
        </div>
        <BenefitRulesTable
          data={data}
          total={total}
          page={filters.page}
          limit={filters.limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
          filters={filters}
          onApplyFilters={applyFilters}
          onClearFilters={clearFilters}
        />
      </div>
    </div>
  )
}
